import { clsx } from 'clsx'

import {
  BillingPriceInterval,
  type BillingProduct,
} from '@tribeplatform/gql-client/types'
import { T } from '@tribeplatform/react-components/i18n'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { Link } from '@tribeplatform/react-ui-kit/Link'

import { useCurrentProductInformation } from '../hooks/useCurrentProductInformation.js'
import { PlanCardButton } from '../PlansPage/PlanCardButton.js'
import { PlanCardButtonLabel } from '../PlansPage/PlanCardButtonLabel.js'

import { PlanPricing } from './PlanPricing.js'
import { getPlanPricing } from './pricing.js'
import { TrialStatusAlert } from './TrialStatusAlert.js'

interface Props {
  product: BillingProduct
  interval: BillingPriceInterval
}

export const PlanCard = ({ product, interval }: Props) => {
  const currentPricing = getPlanPricing(product, interval)
  const yearlyPricing = getPlanPricing(product, BillingPriceInterval.Year)
  const monthlyPricing = getPlanPricing(product, BillingPriceInterval.Month)
  const { monthlyAmount, monthlyPrice, price, savingsPercent, yearlyTotal } =
    currentPricing
  const reservedMonthlyAmount = Math.max(
    monthlyPricing.monthlyAmount,
    yearlyPricing.monthlyAmount,
  )
  const { currentProduct, isTrial } = useCurrentProductInformation()
  const isTrialPlan = isTrial && currentProduct?.upgradedPlanId === product.id

  return (
    <div
      className={clsx(
        'relative flex h-full flex-col rounded-[18px] border px-5 pb-5 pt-6',
        product.popular
          ? 'border-line-positive-subdued bg-[linear-gradient(180deg,var(--bm-color-surface-positive-subdued)_0%,var(--bm-color-surface)_70%)] shadow-sm'
          : 'border-line-subdued bg-surface',
      )}
    >
      {product.popular && (
        <div className="absolute left-4 top-0 -translate-y-1/2">
          <span className="inline-block rounded-full bg-surface-positive px-2.5 py-1 text-[11px] font-medium text-content-on-positive">
            <T
              defaultMessage="Recommended"
              id="Admin.Billing.PlanCards.Recommended"
            />
          </span>
        </div>
      )}

      <div className="pb-3">
        <h3 className="text-[1.0625rem] font-medium text-content">
          {product.name}
        </h3>
      </div>

      <div className="pb-5">
        {product.isEnterprise ? (
          <div className="text-[2.125rem] font-semibold tracking-[-0.03em] text-content">
            <T defaultMessage="Contact us" id="Generics.ContactUs" />
          </div>
        ) : (
          <PlanPricing
            monthlyAmount={monthlyAmount}
            interval={interval}
            monthlyPrice={monthlyPrice}
            reservedMonthlyAmount={reservedMonthlyAmount}
            reservedSavingsPercent={yearlyPricing.savingsPercent}
            savingsPercent={savingsPercent}
            yearlyTotal={yearlyTotal}
          />
        )}
      </div>

      <div className="flex flex-grow flex-col gap-2.5">
        {product.features.map(feature => (
          <div
            key={feature}
            className="flex items-start gap-2 text-content-subdued"
          >
            <div className="mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border border-line-subdued">
              <SvgIcon name="check" className="size-2.5 text-content-subdued" />
            </div>
            <span className="text-xs leading-relaxed text-content-subdued">
              {feature}
            </span>
          </div>
        ))}
      </div>

      {product.imageUrls?.length > 0 && (
        <div className="pb-5 pt-6">
          <p className="mb-2 text-[11px] font-medium uppercase tracking-[0.08em] text-content-subdued">
            <T
              defaultMessage="integrations"
              id="Admin.Billing.PlanCards.Integrations"
            />
          </p>
          <div className="flex flex-wrap gap-1.5">
            {product.imageUrls.map(url => (
              <img
                key={url}
                src={url}
                alt=""
                className="size-[18px] rounded-sm object-contain"
              />
            ))}
          </div>
        </div>
      )}

      <div className="mt-auto flex flex-col gap-2 pt-6">
        {isTrialPlan && <TrialStatusAlert />}
        {product.isEnterprise || !price ? (
          <a
            href="https://bettermode.com/contact-sales"
            target="_blank"
            rel="noopener noreferrer"
            className="flex h-11 items-center justify-center rounded-full bg-surface-inverse px-4 text-sm font-medium text-content-on-inverse transition hover:opacity-90"
          >
            <PlanCardButtonLabel variant="contact" />
          </a>
        ) : (
          <PlanCardButton
            newProduct={product}
            price={price}
            className={clsx(
              'flex h-11 items-center justify-center rounded-full text-sm !border-transparent shadow-none',
              product.popular
                ? '!bg-action-primary !text-content-on-interactive hover:!bg-action-primary-hovered'
                : '!bg-surface-inverse !text-content-on-inverse hover:!opacity-90',
            )}
          />
        )}
        <div className="flex justify-center">
          <Link
            href="https://bettermode.com/pricing"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-content-subdued hover:text-content"
          >
            <T
              defaultMessage="See details"
              id="Admin.Billing.PlanCards.SeeDetails"
            />
            <SvgIcon name="chevron-right" className="size-3" />
          </Link>
        </div>
      </div>
    </div>
  )
}
